import { useEffect, useRef, useState, type ReactNode } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/** A removable active-filter chip shown beneath the header. */
export function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-iris/30 bg-iris/10 py-0.5 pl-2.5 pr-1.5 text-[0.7rem] font-semibold text-iris">
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear ${label}`}
        className="text-iris/70 transition-colors hover:text-iris"
      >
        <X aria-hidden className="h-3 w-3" />
      </button>
    </span>
  );
}

/**
 * A consolidated Filters popover: one labelled trigger with an active-count badge
 * that opens a panel of `FilterDropdown`s, so a toolbar carries a single control
 * instead of several. Closes on outside-click / Escape. Shared across pages.
 */
export function FiltersPopover({
  count,
  onClear,
  children,
}: {
  count: number;
  onClear: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors',
          count > 0
            ? 'border-iris/50 bg-iris/10 text-bone'
            : 'border-border text-haze hover:border-border-strong hover:text-bone',
        )}
      >
        <SlidersHorizontal aria-hidden className="h-3.5 w-3.5" />
        Filters
        {count > 0 && (
          <span className="rounded-full bg-iris px-1.5 text-[0.6rem] font-bold text-on-iris">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-60 rounded-md border border-hairline bg-surface p-3 shadow-pop"
        >
          <div className="mb-2.5 flex items-center justify-between">
            <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
              Filters
            </span>
            {count > 0 && (
              <button
                type="button"
                onClick={onClear}
                className="text-[0.7rem] font-semibold text-muted transition-colors hover:text-haze"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-col items-start gap-2">{children}</div>
        </div>
      )}
    </div>
  );
}
